import React from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { ConnectedProps, connect } from "react-redux";
import Common from "us.common";
import { ConfigurationProvider } from "us.common/ConfigurationContext";
import commonRoutes from "us.common/routes";
import { SignalRProvider } from "us.common/SignalRProvider";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { uscRoutes } from "routes";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { HeaderExtra, HeaderSettings } from "components";
import { RootState } from "us.helper/types";
import { Loader, LoginRedirect } from "us.common/components";
import { useAuth } from "us.helper/hooks/useAuth";
import { DrawerProvider } from "us.common/context";

const { Page404, AppLayout, ConfigProvider } = Common.Components;

const AppInner: React.FC<PropsFromRedux> = ({ currentLanguage }) => {
  const { pathname } = useLocation();
  const isEditor =
    pathname === "/template-editor" || pathname === "/ehf-template-editor";
  const isEnabledSearch = !isEditor; // enabled everywhere except editors
  const isEnabledSettings = !isEditor && pathname === "/";

  return (
    <ConfigurationProvider>
      <SignalRProvider>
        <DrawerProvider>
          <AppLayout
            headerExtra={!isEnabledSearch ? <HeaderExtra /> : undefined}
            megaMenu={true}
            enableSearch={isEnabledSearch}
            enableSettings={isEnabledSettings}
            headerSettings={<HeaderSettings />}
          >
            <ConfigProvider locale={currentLanguage as any}>
              <DndProvider backend={HTML5Backend}>
                <Routes>
                  {[...uscRoutes(), ...commonRoutes]}
                  <Route path="*" element={<Page404 />} />
                </Routes>
              </DndProvider>
            </ConfigProvider>
          </AppLayout>
        </DrawerProvider>
      </SignalRProvider>
    </ConfigurationProvider>
  );
};
// Wrapper to provide BrowserRouter so we can use useLocation inside AppInner
const App: React.FC<PropsFromRedux> = ({ currentLanguage }) => {
  const { isAuthenticated, isInitialized } = useAuth();

  if (!isInitialized) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedTemplate>
        <BrowserRouter
          future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
        >
          <LoginRedirect />
        </BrowserRouter>
      </UnauthenticatedTemplate>
    );
  }

  return (
    <AuthenticatedTemplate>
      <BrowserRouter
        future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
      >
        <AppInner currentLanguage={currentLanguage} />
      </BrowserRouter>
    </AuthenticatedTemplate>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentLanguage: state.common.currentLanguage,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(App);
